import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import { API } from "../api";

import { toasts } from "../components/AppToast";

import Layout from "../components/Layout";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiPlus,
  FiCalendar,
  FiSearch,
} from "react-icons/fi";

export default function SeasonName() {
  const navigate = useNavigate();

  // =========================================================
  // USER
  // =========================================================

  const [user, setUser] = useState(null);

  // =========================================================
  // TABLE
  // =========================================================

  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);

  // =========================================================
  // FORM
  // =========================================================

  const [name, setName] = useState("");

  const [editId, setEditId] = useState(null);

  const [saving, setSaving] = useState(false);

  // =========================================================
  // DELETE MODAL
  // =========================================================

  const [delModal, setDelModal] = useState(null);

  const [delSaving, setDelSaving] = useState(false);

  // =========================================================
  // AUTH
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setUser(
        JSON.parse(
          localStorage.getItem("user") || "null"
        )
      );
    } catch (error) {
      console.error(
        "User parse error:",
        error
      );

      setUser(null);
    }
  }, [navigate]);

  // =========================================================
  // FETCH DATA
  // =========================================================

  const fetchData = useCallback(
    async (pg = 1, q = "") => {
      setLoading(true);

      try {
        const res = await API.get(
          "/seasons",
          {
            params: {
              page: pg,
              limit: 10,
              search: q,
            },
          }
        );

        console.log(
          "Season pagination response:",
          res.data
        );

        setData(
          res.data?.data || []
        );

        setTotal(
          Number(
            res.data?.total || 0
          )
        );

        setTotalPages(
          Number(
            res.data?.totalPages || 1
          )
        );
      } catch (err) {
        console.error(
          "Failed to load seasons:",
          err
        );

        toasts.error(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load seasons"
        );

        setData([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // =========================================================
  // INITIAL LOAD + PAGE CHANGE
  // =========================================================

  useEffect(() => {
    const token =
      localStorage.getItem("token");

    if (!token) {
      return;
    }

    fetchData(
      page,
      search
    );
  }, [page, fetchData,search]);

  // =========================================================
  // SEARCH
  // =========================================================

  useEffect(() => {
    const timer =
      setTimeout(() => {
        setPage(1);

        fetchData(
          1,
          search
        );
      }, 350);

    return () =>
      clearTimeout(timer);
  }, [search, fetchData]);

  // =========================================================
  // ADD / UPDATE
  // =========================================================

  const handleSubmit = async () => {
    if (!name.trim()) {
      toasts.warning(
        "Enter season name"
      );

      return;
    }

    if (!user?.user_id) {
      toasts.error(
        "User information not found"
      );

      return;
    }

    setSaving(true);

    try {
      // -----------------------------------------------------
      // UPDATE
      // -----------------------------------------------------

      if (editId) {
        await API.put(
          `/seasons/${editId}`,
          {
            name: name.trim(),
            user_id:
              user.user_id,
          }
        );

        toasts.success(
          "Season updated successfully"
        );
      }

      // -----------------------------------------------------
      // ADD
      // -----------------------------------------------------

      else {
        await API.post(
          "/seasons",
          {
            name: name.trim(),
            user_id:
              user.user_id,
          }
        );

        toasts.success(
          "Season added successfully"
        );
      }

      // Reset form
      setName("");
      setEditId(null);

      // Reload current page
      await fetchData(
        page,
        search
      );
    } catch (err) {
      console.error(
        "Submit error:",
        err
      );

      toasts.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (row) => {
    setName(
      row.season_name || ""
    );

    setEditId(
      row.season_id
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // CANCEL EDIT
  // =========================================================

  const handleCancel = () => {
    setName("");
    setEditId(null);
  };

  // =========================================================
  // OPEN DELETE MODAL
  // =========================================================

  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================

  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async () => {
    if (!delModal?.season_id) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(
        `/seasons/${delModal.season_id}`
      );

      toasts.success(
        "Season deleted successfully"
      );

      setDelModal(null);

      // -----------------------------------------------------
      // Calculate remaining records
      // -----------------------------------------------------

      const remainingRecords =
        Math.max(
          0,
          total - 1
        );

      const newTotalPages =
        Math.max(
          1,
          Math.ceil(
            remainingRecords / 10
          )
        );

      // -----------------------------------------------------
      // If current page no longer exists,
      // go to previous page.
      // -----------------------------------------------------

      if (
        page > newTotalPages
      ) {
        setPage(
          newTotalPages
        );

        return;
      }

      // -----------------------------------------------------
      // Otherwise reload current page.
      // -----------------------------------------------------

      await fetchData(
        page,
        search
      );
    } catch (err) {
      console.error(
        "Delete error:",
        err
      );

      toasts.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // =========================================================
  // PREVIOUS PAGE
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // NEXT PAGE
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Layout activePage="Season Details">

      <main className="dashboard-main">

        {/* =================================================
            CARD
        ================================================= */}

        <div className="season-card">

          {/* =================================================
              TOP CONTROLS
          ================================================= */}

          <div className="season-top-row">

            {/* =================================================
                FORM
            ================================================= */}

            <div className="season-form-row">

              {editId && (
                <span className="season-editing-badge">
                  Editing #{editId}
                </span>
              )}

              <input
                type="text"
                placeholder="Enter Season Name"
                value={name}
                onChange={(e) =>
                  setName(
                    e.target.value
                  )
                }
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter"
                  ) {
                    handleSubmit();
                  }
                }}
                className="season-input"
              />

              <input
                type="hidden"
                value={
                  user?.user_id || ""
                }
                readOnly
              />

              {editId && (
                <button
                  type="button"
                  onClick={
                    handleCancel
                  }
                  className="season-cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>
              )}

              <button
                type="button"
                onClick={
                  handleSubmit
                }
                className="um-btn-primary"
                disabled={saving}
              >

                {saving ? (

                  <span className="spinner" />

                ) : editId ? (

                  <FiEdit />

                ) : (

                  <FiPlus />

                )}

                {saving
                  ? "Saving..."
                  : editId
                  ? "Update Season Name"
                  : "Add Season Name"}

              </button>

            </div>

            {/* =================================================
                SEARCH
            ================================================= */}

            <div className="search-wrapper">

              <span className="search-icon">

                <FiSearch
                  size={17}
                />

              </span>

              <input
                className="search-input"
                placeholder="Search seasons..."
                value={search}
                onChange={(e) => {
                  setSearch(
                    e.target.value
                  );

                  // Search always starts
                  // from page 1.
                  setPage(1);
                }}
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() =>
                    setSearch("")
                  }
                >
                  ✕
                </button>
              )}

            </div>

          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">

            <table className="table table-striped table-hover table-bordered align-middle mb-0">

              <thead className="table-light">

                <tr className="um-table-head">

                  <th className="um-th text-nowrap">
                    #
                  </th>

                  <th className="um-th text-nowrap">
                    Season Name
                  </th>

                  <th className="um-th text-nowrap">
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {/* =================================================
                    LOADING
                ================================================= */}

                {loading ? (

                  <tr>

                    <td
                      colSpan={3}
                      className="um-td text-center py-4"
                    >
                      Loading...
                    </td>

                  </tr>

                ) : data.length === 0 ? (

                  /* =================================================
                      EMPTY
                  ================================================= */

                  <tr>

                    <td
                      colSpan={3}
                      className="empty-table-cell text-center py-5"
                    >

                      <div className="fs-2 mb-2">

                        <FiCalendar />

                      </div>

                      <div className="fw-semibold">

                        No seasons found

                      </div>

                      {search && (
                        <div className="text-muted">

                          Try another
                          search term.

                        </div>
                      )}

                    </td>

                  </tr>

                ) : (

                  /* =================================================
                      DATA
                  ================================================= */

                  data.map(
                    (row, i) => (

                      <tr
                        key={`season-${row.season_id}`}
                        className={`um-row ${
                          editId ===
                          row.season_id
                            ? "editing-row"
                            : ""
                        }`}
                      >

                        {/* NUMBER */}

                        <td className="um-td user-number text-center">

                          {String(
                            (page - 1) *
                              10 +
                              i +
                              1
                          ).padStart(
                            2,
                            "0"
                          )}

                        </td>

                        {/* NAME */}

                        <td className="um-td text-center">

                          <span className="fw-semibold">

                            {row.season_name ||
                              "—"}

                          </span>

                        </td>

                        {/* ACTION */}

                        <td className="um-td text-center season-action-td">

                          <div className="action-buttons d-flex justify-content-center align-items-center gap-2">

                            {/* EDIT */}

                            <button
                              type="button"
                              onClick={() =>
                                handleEdit(
                                  row
                                )
                              }
                              className="action-btn action-edit btn btn-sm"
                              title="Edit"
                            >

                              <FiEdit />

                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              onClick={() =>
                                openDeleteModal(
                                  row
                                )
                              }
                              className="action-btn action-delete btn btn-sm"
                              title="Delete"
                            >

                              <FiTrash2 />

                            </button>

                          </div>

                        </td>

                      </tr>

                    )
                  )

                )}

              </tbody>

            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />

          </div>

        </div>

      </main>

      {/* =====================================================
          DELETE MODAL
      ====================================================== */}

      {delModal && (

        <div
          className="um-overlay"
          onClick={
            closeDeleteModal
          }
        >

          <div
            className="um-modal um-delete-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="delete-content">

              {/* ICON */}

              <div className="delete-icon">

                <FiTrash2
                  size={32}
                />

              </div>

              {/* TITLE */}

              <h2 className="delete-title">

                Delete Season?

              </h2>

              {/* DESCRIPTION */}

              <p className="delete-description">

                Are you sure you want
                to delete{" "}

                <span className="badge badge-success">

                  "{delModal.season_name}"

                </span>

                ?

                <br />

                This will permanently
                remove this season.

                <br />

                <strong>
                  This action cannot
                  be undone.
                </strong>

              </p>

              {/* ACTIONS */}

              <div className="delete-actions">

                {/* CANCEL */}

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={
                    closeDeleteModal
                  }
                  disabled={
                    delSaving
                  }
                >
                  Cancel
                </button>

                {/* DELETE */}

                <button
                  type="button"
                  disabled={
                    delSaving
                  }
                  className="delete-confirm-btn"
                  onClick={
                    handleDelete
                  }
                >

                  {delSaving ? (

                    <span className="spinner" />

                  ) : (

                    <>
                      <FiTrash2 />

                      Delete Permanently
                    </>

                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

   
    </Layout>
  );
}
